import React, {useEffect, useMemo, useState} from "react";
import {useLazyQuery} from "@apollo/client";
import {Box, Chip, Tooltip} from "@mui/material";
import {grey} from "@mui/material/colors";
import Face3Icon from "@mui/icons-material/Face3";
import Face5Icon from "@mui/icons-material/Face5";
import FaceIcon from "@mui/icons-material/Face";

import ChildDetailsTooltip from "./ChildDetailsTooltip";
import InternalTagsDialog from "../common/InternalTagsDialog";
import {ApolloFetchPolicies} from "../../constants/FetchPolicies";
import {buildChildDisplayName, buildTagCountsBySentiment} from "../../helpers/SessionHelper";
import {TAG_COLORS} from "../../helpers/MemberTagHelper";
import {FETCH_CHILD_TAG_METRICS} from "../../queries/GraphQLQueries";
import '../../css/App.css';

const styles = {
    childContainer: {
        flexWrap: 'nowrap',
        alignItems: 'center',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflowY: 'hidden',
    },
    cardContainer: {
        borderRadius: '0.75rem',
        border: `0.0625rem solid ${grey[300]}` ,
        display: 'flex',
        flexDirection: 'column',
        marginLeft: '0.1rem',
        overflow: 'hidden',
    },
    childChip: {
        backgroundColor: 'transparent',
        borderRadius: 0,
        boxShadow: 'none',
        display: 'flex',
        overflow: 'hidden',
        width: '100%',
        marginBottom: '0.1rem',
    },
    iconWrapper: {
        display: 'flex',
        paddingLeft: '0.5rem'
    },
    tagsBox: {
        alignItems: 'center',
        display: 'flex',
        gap: '0.25rem',
        justifyContent: 'center',
        maxHeight: '1.5rem',
        paddingBottom: '0.25rem',
    },
    tagChip: {
        height: '1rem',
        fontSize: '0.7rem',
        color: 'white',
        backgroundColor: TAG_COLORS.POSITIVE
    },
    tooltip: {
        bgcolor: 'white',
        color: 'black',
    }
};

export default function ChildChipWithTagBadges({child, serviceType, attendanceReport, bookingId, sessionStartTime}) {
    const [isTagsDialogOpen, setIsTagsDialogOpen] = useState(false);
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

    const [fetchChildTagMetrics, {data}] = useLazyQuery(FETCH_CHILD_TAG_METRICS, {
        fetchPolicy: ApolloFetchPolicies.NETWORK_ONLY
    });

    useEffect(() => {
        if (child?.id) {
            fetchChildTagMetrics({
                variables: {childId: child.id}
            });
        }
    }, [child.id, fetchChildTagMetrics]);

    const tagMetrics = useMemo(() => {
        if (!data?.fetchChildTagMetrics) {
            return null;
        }

        return data.fetchChildTagMetrics.filter(tagMetric => tagMetric.tag.isInternal);
    }, [data?.fetchChildTagMetrics]);

    const tagCountPerSentiment = useMemo(() => {
        return buildTagCountsBySentiment(tagMetrics);
    }, [tagMetrics]);

    const handleOpenTagsDialog = () => {
        setIsTagsDialogOpen(true);
        setIsTooltipOpen(false);
    };

    const handleCloseTagsDialog = () => {
        setIsTagsDialogOpen(false);
        fetchChildTagMetrics({
            variables: {childId: child.id}
        });
    };

    const childIcon = (gender) => {
        let icon = <Face5Icon/>;

        if (gender === 'MALE') {
            icon = <FaceIcon/>;
        } else if (gender === 'FEMALE') {
            icon = <Face3Icon/>;
        }

        return icon;
    }

    const childIconInChip = <Box sx={styles.iconWrapper}>{childIcon(child.gender)}</Box>

    return (
        <>
            <Box sx={styles.childContainer}>
                <Box sx={styles.cardContainer}>
                    <Tooltip
                        open={isTooltipOpen}
                        onClose={() => setIsTooltipOpen(false)}
                        onMouseEnter={() => setIsTooltipOpen(true)}
                        disableFocusListener
                        arrow
                        slotProps={{tooltip: {sx: styles.tooltip}}}
                        title={<ChildDetailsTooltip {...{
                            child,
                            serviceType,
                            attendanceReport,
                            tagMetrics,
                            onOpenTagsDialog: handleOpenTagsDialog
                        }} />}>
                        <Chip icon={childIconInChip} size="small" label={buildChildDisplayName(child)} sx={styles.childChip}/>
                        <Box sx={styles.tagsBox}>
                            <Chip label={tagCountPerSentiment.positiveCount} size="small" sx={styles.tagChip}/>
                            <Chip label={tagCountPerSentiment.negativeCount} size="small" sx={{...styles.tagChip, backgroundColor: TAG_COLORS.NEGATIVE}}/>
                        </Box>
                    </Tooltip>
                </Box>
            </Box>

            <InternalTagsDialog
                isTagsDialogOpen={isTagsDialogOpen}
                handleCloseTagsDialog={handleCloseTagsDialog}
                child={child}
                tagMetrics={tagMetrics}
                bookingId={bookingId}
                sessionStartTime={sessionStartTime}
                onTagsUpdated={() => fetchChildTagMetrics({variables: {childId: child.id}})}/>
        </>
    );
}